import { Button, Dialog, RadioGroup, Input } from "@/components/ui";
import { AccountOption } from "@/services/bedrock";
import React, { useMemo } from "react";
import { useDebounced } from "@/utils";

interface Props {
  open: boolean;
  accountOptions: AccountOption[];
  onCancel: () => void;
  onConfirm: (option: AccountOption) => void;
}

export function AccountSelectionDialog({ open, accountOptions, onCancel, onConfirm }: Props) {
  const [selectedOption, setSelectedOption] = React.useState<AccountOption>();
  const [searchQuery, setSearchQuery] = React.useState("");
  const debouncedSearch = useDebounced(searchQuery, 300);

  // const testOptions = useMemo(() => {
  //   if (!accountOptions) return [];
  //   const manyOptions: AccountOption[] = [];
  //   for (let i = 0; i < 100; i++) {
  //     accountOptions.forEach(option => {
  //       manyOptions.push({
  //         ...option,
  //         id: `${option.id}-${i}`,
  //         name: `${option.name} (Copy ${i})`,
  //       });
  //     });
  //   }
  //   return manyOptions;
  // }, [accountOptions]);

  const showSearch = accountOptions?.length > 5;
  const filteredOptions = useMemo(() => {
    if (!accountOptions) return [];
    return accountOptions
      .filter(option => option.name.toLowerCase().includes(debouncedSearch.toLowerCase()))
      .sort((a, b) => Number(!!a.disabled) - Number(!!b.disabled));
  }, [accountOptions, debouncedSearch]);

  const selectOption = (option: AccountOption) => {
    if (option.disabled) return;
    setSelectedOption(option);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={open => {
        if (!open) {
          setSearchQuery("");
          onCancel();
        }
      }}
    >
      <Dialog.Header>
        <Dialog.Title>Choose an account</Dialog.Title>
      </Dialog.Header>
      <Dialog.Content className="flex flex-col gap-4">
        {showSearch && (
          <Input
            placeholder="Search accounts..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            autoFocus
          />
        )}
        <div className="max-h-[300px] min-h-[100px] overflow-y-auto">
          <RadioGroup
            value={selectedOption}
            onValueChange={v => selectOption(v as AccountOption)}
            className="gap-2 pr-2"
          >
            {filteredOptions?.map(option => (
              <RadioGroup.Button
                value={option}
                disabled={option.disabled}
                id={`account-option-${option.id}`}
                label={
                  <div className="flex flex-col items-start gap-0 text-base">
                    <span className="font-semibold">{option.name}</span>
                    {option.description && <span className="text-sm text-muted-foreground">{option.description}</span>}
                    {option.disabled && option.warningDescription && (
                      <span className="text-sm text-amber-800 mt-1">{option.warningDescription}</span>
                    )}
                  </div>
                }
              />
            ))}
            {filteredOptions?.length === 0 && (
              <div className="text-center py-4 text-muted-foreground">No accounts found</div>
            )}
          </RadioGroup>
        </div>
      </Dialog.Content>
      <Dialog.Footer>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={() => onConfirm(selectedOption!)} disabled={!selectedOption}>
          OK
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
}
